import React from 'react';
import {
  Box,
  Chip,
  Link,
  Stack,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import CancelRoundedIcon from '@mui/icons-material/CancelRounded';
import DataObjectRoundedIcon from '@mui/icons-material/DataObjectRounded';
import {
  formatDateTime,
  getFieldLabel,
  getSchemaFields,
  hasRenderableValue,
  toTitleCase,
} from './messageUtils';

const isIsoDate = (value) => (
  typeof value === 'string' &&
  /^\d{4}-\d{2}-\d{2}(?:[Tt].*)?/.test(value)
);

const isUrl = (value) => (
  typeof value === 'string' &&
  /^https?:\/\//i.test(value)
);

const isEmail = (value) => (
  typeof value === 'string' &&
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
);

const formatPrimitive = (value, field) => {
  if (field?.type === 'date' || field?.type === 'datetime' || isIsoDate(value)) {
    return formatDateTime(value);
  }

  if (typeof value === 'number') {
    return Number.isInteger(value) ? value.toLocaleString() : value.toLocaleString(undefined, {
      maximumFractionDigits: 6,
    });
  }

  return String(value);
};

const ValueDisplay = ({ value, field, compact = false }) => {
  if (typeof value === 'boolean') {
    return (
      <Chip
        size="small"
        icon={value ? <CheckCircleRoundedIcon /> : <CancelRoundedIcon />}
        color={value ? 'success' : 'default'}
        variant={value ? 'filled' : 'outlined'}
        label={value ? 'Yes' : 'No'}
      />
    );
  }

  if (Array.isArray(value)) {
    if (!value.length) {
      return <Typography variant="body2" color="text.secondary">None</Typography>;
    }

    if (value.every((item) => ['string', 'number', 'boolean'].includes(typeof item))) {
      return (
        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
          {value.map((item) => (
            <Chip key={String(item)} size="small" variant="outlined" label={String(item)} />
          ))}
        </Stack>
      );
    }

    return (
      <Stack spacing={1}>
        {value.map((item, index) => (
          <Box
            key={`${field?.name || 'item'}-${index}`}
            sx={{
              p: 1,
              borderRadius: 2,
              bgcolor: (theme) => alpha(theme.palette.background.default, 0.72),
            }}
          >
            <ValueDisplay value={item} compact />
          </Box>
        ))}
      </Stack>
    );
  }

  if (value && typeof value === 'object') {
    const entries = Object.entries(value).filter(([, entryValue]) => hasRenderableValue(entryValue));

    if (!entries.length) {
      return <Typography variant="body2" color="text.secondary">None</Typography>;
    }

    return (
      <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
        {entries.map(([key, entryValue]) => {
          const primitiveValue = entryValue && typeof entryValue === 'object'
            ? JSON.stringify(entryValue)
            : String(entryValue);

          return (
            <Chip
              key={key}
              size="small"
              icon={<DataObjectRoundedIcon />}
              label={`${toTitleCase(key)}: ${primitiveValue}`}
              variant="outlined"
              sx={{ maxWidth: compact ? '100%' : 320 }}
            />
          );
        })}
      </Stack>
    );
  }

  if (isUrl(value)) {
    return (
      <Link href={value} target="_blank" rel="noopener noreferrer" variant="body2" sx={{ wordBreak: 'break-word' }}>
        {value}
      </Link>
    );
  }

  if (isEmail(value)) {
    return (
      <Link href={`mailto:${value}`} variant="body2" sx={{ wordBreak: 'break-word' }}>
        {value}
      </Link>
    );
  }

  return (
    <Typography
      variant="body2"
      sx={{
        fontWeight: compact ? 500 : 600,
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
      }}
    >
      {formatPrimitive(value, field)}
    </Typography>
  );
};

const buildDisplayFields = ({ body, schema, hiddenFields }) => {
  const hidden = new Set(hiddenFields);
  const schemaFields = getSchemaFields(schema);
  const displayFields = [];
  const seen = new Set();

  schemaFields.forEach((field) => {
    const name = field?.name;
    if (!name || hidden.has(name) || !hasRenderableValue(body[name])) {
      return;
    }

    seen.add(name);
    displayFields.push({
      key: name,
      label: getFieldLabel(field, name),
      value: body[name],
      field,
      fromSchema: true,
    });
  });

  Object.entries(body).forEach(([name, value]) => {
    if (seen.has(name) || hidden.has(name) || !hasRenderableValue(value)) {
      return;
    }

    displayFields.push({
      key: name,
      label: toTitleCase(name),
      value,
      field: { name, type: typeof value },
      fromSchema: false,
    });
  });

  return displayFields;
};

export default function SchemaBodyFields({
  body = {},
  schema = null,
  columns = { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
  hiddenFields = [],
  compact = false,
}) {
  const displayFields = buildDisplayFields({ body, schema, hiddenFields });

  if (!displayFields.length) {
    return (
      <Typography variant="body2" color="text.secondary">
        No message body fields.
      </Typography>
    );
  }

  if (compact) {
    return (
      <Stack
        spacing={0}
        sx={{
          borderRadius: 1.5,
          border: (theme) => `1px solid ${alpha(theme.palette.divider, 0.72)}`,
          overflow: 'hidden',
          bgcolor: (theme) => alpha(theme.palette.background.paper, 0.58),
        }}
      >
        {displayFields.map(({ key, label, value, field }, index) => (
          <Box
            key={key}
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'minmax(128px, 0.34fr) minmax(0, 1fr)' },
              gap: { xs: 0.4, sm: 1.25 },
              p: 1.2,
              borderTop: index === 0
                ? 'none'
                : (theme) => `1px solid ${alpha(theme.palette.divider, 0.62)}`,
            }}
          >
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 750 }}>
              {label}
            </Typography>
            <Box sx={{ minWidth: 0 }}>
              <ValueDisplay value={value} field={field} compact />
            </Box>
          </Box>
        ))}
      </Stack>
    );
  }

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: columns,
        gap: compact ? 1 : 1.25,
      }}
    >
      {displayFields.map(({ key, label, value, field }) => (
        <Box
          key={key}
          sx={{
            minWidth: 0,
            p: compact ? 1.25 : 1.5,
            borderRadius: 2,
            border: (theme) => `1px solid ${alpha(theme.palette.divider, 0.9)}`,
            bgcolor: (theme) => alpha(theme.palette.background.paper, 0.72),
          }}
        >
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', mb: 0.5, fontWeight: 700 }}
          >
            {label}
          </Typography>
          <ValueDisplay value={value} field={field} compact={compact} />
        </Box>
      ))}
    </Box>
  );
}
